import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  collection, doc, onSnapshot, setDoc, deleteDoc, getDoc,
  serverTimestamp, query, orderBy, getDocs, writeBatch, getCountFromServer
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { enqueuePatch, loadQueue } from '../lib/offlineQueue';
import { writeListWithConflictDetection } from '../lib/historyConflict';
import { getClientId } from '../lib/clientId';
import { describeCallableFailure } from '../lib/callableDiagnostics';
import { logSync, logHeartbeat } from '../lib/syncLog';
import { recordContent } from '../lib/contentTrail';
import { noteSaveFailure, noteSaveSuccess } from '../lib/saveHealth';
import { saveViaApi } from '../lib/saveViaApi';
import { fetchListChanges } from '../lib/readViaApi';
import { startReadHealth, subscribeReadHealth, areReadsDead } from '../lib/readHealth';
import { noteClientError } from '../lib/clientHealth';

// Walter 2026-05-31 (free-tier cap): total lists across all of a
// user's boards, including archived (archive is a `state` flag, not a
// move, so archived lists are still in the subcollection). Uses
// Firestore count aggregation — one cheap read per board (boards are
// themselves capped at FREE_TIER.maxBoards, so <=10 reads). Returns a
// number; on any error returns null so callers can fail OPEN (a soft
// cap should never block a legit create just because a count read
// hiccuped).
export async function countAllLists(ownerUid, boards) {
  if (!ownerUid || !Array.isArray(boards) || boards.length === 0) return 0;
  try {
    const counts = await Promise.all(
      boards.map(async (b) => {
        const c = await getCountFromServer(
          collection(db, 'users', ownerUid, 'boards', b.id, 'lists')
        );
        return c.data().count || 0;
      })
    );
    return counts.reduce((a, n) => a + n, 0);
  } catch (e) {
    console.warn('[countAllLists] failed (failing open):', e?.message || e);
    return null;
  }
}

// ── BOARDS ──────────────────────────────────────────────────
export function useBoards(userId) {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, 'users', userId, 'boards'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by `order` when set (newer field, used by drag-reorder),
      // falling back to `createdAt` order for legacy boards that
      // pre-date the field. Boards without `order` always sort AFTER
      // any with one — that way reordered boards keep their position
      // even when new (unordered) ones get added.
      docs.sort((a, b) => {
        const aHas = typeof a.order === 'number';
        const bHas = typeof b.order === 'number';
        if (aHas && bHas) return a.order - b.order;
        if (aHas) return -1;
        if (bHas) return 1;
        return 0; // both unordered — preserve query's createdAt order
      });
      setBoards(docs);
      setLoading(false);
    }, (err) => {
      // Without an error handler, a permission-denied (or other)
      // snapshot failure would leave loading=true forever and the
      // app stuck on "loading boards…". Surface as empty + log so
      // the user can at least see the empty-state UI.
      console.warn('[useBoards] snapshot error', err);
      setBoards([]);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  const addBoard = useCallback(async (name) => {
    if (!userId) return;
    const ref = doc(collection(db, 'users', userId, 'boards'));
    // Seed `mode: 'free'` explicitly so new boards are unambiguous. Existing
    // boards without the field fall back to 'free' at render time in
    // BoardPage — the explicit seed just saves the fallback check once and
    // makes the field visible in the Firestore console.
    //
    // ownerId is redundant with the path (we know it's at users/{ownerId}/...)
    // but it lets future cross-user collectionGroup queries filter by owner
    // without needing to parse the path string. ACL fields default empty —
    // editing them is owner-only via the Share modal.
    await setDoc(ref, {
      name,
      mode: 'free',
      ownerId: userId,
      editors: [],
      viewers: [],
      shareToken: null,
      shareTokenRole: 'viewer',
      createdAt: serverTimestamp(),
    });
    return ref.id;
  }, [userId]);

  // Create a board AND seed its lists subcollection from a template
  // definition (see lib/boardTemplates.js). Used by the welcome-setup
  // popup at first sign-in and by the new-board picker. Falls through to
  // a plain blank board if the template has no lists, so callers don't
  // need to special-case "blank".
  const addBoardFromTemplate = useCallback(async (template, customName) => {
    if (!userId) return;
    const boardRef = doc(collection(db, 'users', userId, 'boards'));
    const boardData = {
      name: customName || template.defaultBoardName || template.label || 'untitled',
      mode: template.mode || 'free',
      // Sharing ACL fields — see addBoard above for why these exist.
      ownerId: userId,
      editors: [],
      viewers: [],
      shareToken: null,
      shareTokenRole: 'viewer',
      createdAt: serverTimestamp(),
    };
    if (template.mode === 'grid' && template.gridColumnCount) {
      boardData.gridColumnCount = template.gridColumnCount;
    }

    const lists = Array.isArray(template.lists) ? template.lists : [];
    if (lists.length === 0) {
      // Blank — single doc write, no batch needed.
      await setDoc(boardRef, boardData);
      return { boardId: boardRef.id, designatedListIds: [] };
    }

    // Seed lists in a single batch alongside the board doc. One round-trip,
    // atomic from the user's perspective: either the whole template lands
    // or none of it does.
    const batch = writeBatch(db);
    batch.set(boardRef, boardData);

    let zCounter = 0;
    // Collect list IDs that the template flagged as designated reminder
    // lists. Returned to the caller so they can update userPrefs (this
    // hook doesn't own user prefs).
    const designatedListIds = [];
    // Walter 2026-05-26 — same pattern as designatedListIds above, but
    // for the LOG list designation (isDesignatedLogList flag on the
    // template, lands in userPrefs.designatedLogListIds). Returned
    // separately so the caller can dispatch each prefs update without
    // ambiguity over which kind of designation each id refers to.
    const designatedLogListIds = [];
    lists.forEach((tpl, idx) => {
      const listRef = doc(collection(db, 'users', userId, 'boards', boardRef.id, 'lists'));
      zCounter += 1;
      // Walter 2026-05-26 — flag lists seeded with prose content as
      // "demo content" so the panel can render them in a distinct
      // visual treatment (italic body + a small clear-example band).
      // Round-2 tester feedback: even after rewriting the //Note:
      // syntax to natural prose, the seeded content still reads as
      // "stuff I have to clean up." Visual differentiation + a one-
      // click clear makes the demo nature explicit. Only flag lists
      // that actually have seeded content — empty lists shouldn't
      // get the treatment.
      const hasSeededContent = !!(tpl.content && tpl.content.trim().length > 0);
      const listData = {
        title: tpl.title || '',
        content: tpl.content || '',
        // Free-mode positions: prefer explicit x/y/w/h from the template
        // (Simple template uses these for an irregular layout), otherwise
        // fall back to a deterministic auto-grid.
        x: typeof tpl.x === 'number' ? tpl.x : 80 + (idx % 4) * 320,
        y: typeof tpl.y === 'number' ? tpl.y : 60 + Math.floor(idx / 4) * 360,
        w: typeof tpl.w === 'number' ? tpl.w : 280,
        h: typeof tpl.h === 'number' ? tpl.h : 320,
        z: zCounter,
        order: idx + 1,
        state: 'open',
        // Grid placement (only used when board.mode === 'grid').
        gridCol: typeof tpl.gridCol === 'number' ? tpl.gridCol : 0,
        gridRow: typeof tpl.gridRow === 'number' ? tpl.gridRow : idx,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Clears to false the moment the user edits the content OR
        // clicks the band's "clear example" action. Field is omitted
        // entirely when the list isn't seeded with prose (older lists
        // pre-2026-05-26 also don't have it — the rendering layer
        // treats undefined as "not a demo seed").
        ...(hasSeededContent ? { seededFromTemplate: true } : {}),
      };
      batch.set(listRef, listData);
      if (tpl.isDesignatedReminderList) {
        designatedListIds.push(listRef.id);
      }
      if (tpl.isDesignatedLogList) {
        designatedLogListIds.push(listRef.id);
      }
    });

    await batch.commit();
    return { boardId: boardRef.id, designatedListIds, designatedLogListIds };
  }, [userId]);

  const updateBoard = useCallback(async (boardId, data) => {
    if (!userId) return;
    await setDoc(doc(db, 'users', userId, 'boards', boardId), data, { merge: true });
  }, [userId]);

  // Deleting a board must also sweep the lists subcollection — Firestore
  // does NOT cascade. Without this, every board-delete leaves orphaned list
  // docs sitting in Firestore forever (invisible to the UI, still counted
  // against quota).
  // Soft-delete via the `trashBoard` Cloud Function. The function
  // snapshots the board + every list into trash/{ownerUid}/items so
  // the user can restore within 30 days (SHARING_SPEC §3 + §6). Loses
  // ~200ms vs the previous client-side cascade, but gains the
  // safety net. Daily sweep `expireTrashItems` hard-deletes past TTL.
  const deleteBoard = useCallback(async (boardId) => {
    if (!userId) return;
    const fn = httpsCallable(functions, 'trashBoard');
    await fn({ boardId });
  }, [userId]);

  return { boards, loading, addBoard, addBoardFromTemplate, updateBoard, deleteBoard };
}

// ── LISTS (panels on a board) ────────────────────────────────
export function useLists(userId, boardId) {
  const [rawLists, setRawLists] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumped whenever the offline queue mutates, to force a re-overlay.
  const [queueTick, setQueueTick] = useState(0);
  // listId → last server-confirmed updatedAt in ms. Used as the conflict-detection
  // baseline. Only populated from snapshots that came from the server (not cache).
  const lastServerUpdatedAt = useRef(new Map());

  // ── READ FALLBACK (Walter 2026-08-21) ──
  // When a content blocker kills Firestore's Listen channel, NO realtime
  // updates arrive — another device's edits never show up, while writes carry
  // on working over REST and the header still says "synced". That silent
  // deafness is the likely engine behind his conflicts: two devices editing
  // from stale content collide on the next write.
  //
  // So when lib/readHealth concludes reads are dead, poll the same lists
  // through /api/listChanges on listicity.app — first-party, matching no
  // filter rule. Merged into the SAME rawLists state the snapshot feeds, and
  // server-confirmed updatedAts still become conflict baselines, so nothing
  // downstream can tell the difference. Stops the moment reads recover.
  const [readsDead, setReadsDead] = useState(() => areReadsDead());
  useEffect(() => {
    const stop = startReadHealth();
    const off = subscribeReadHealth(setReadsDead);
    return () => { off(); stop(); };
  }, []);

  // ── LISTENER WATCHDOG — REMOVED 2026-09-12 (Walter) ──
  // Shipped 2026-09-06 (#222/#224) to catch a window whose Firestore client
  // had died silently. It caught the wrong thing and did real damage.
  //
  // On his prod log for 2026-09-12 it declared `listener-deaf` at 15:01:52 on
  // a window whose listener was demonstrably ALIVE — server snapshots kept
  // arriving at 15:08:50, 15:11:03, 15:13:52. The verdict then switched that
  // window onto the REST poll below, so for twenty minutes it had TWO sources
  // of truth disagreeing with each other, and it wrote back whichever it saw
  // last. His two windows spent 15:09–15:22 overwriting each other:
  //
  //   15:18:16  adopt  tab  server=32408
  //   15:18:27  saved  pwa  32079        ← 329 characters shorter
  //   15:19:13  adopt  tab  server=32238
  //   15:19:28  adopt  tab  server=32078
  //
  // That is text being destroyed by a diagnostic. The watchdog was
  // speculative from the start — built to catch a CLASS of failure rather
  // than an observed one — and the one failure it was built for (the SDK's
  // INTERNAL ASSERTION killing a client, 2026-09-05) is caught directly and
  // reliably by lib/clientHealth's assertion latch, which stays.
  //
  // The lesson worth keeping: an active prober that can put the app into a
  // different data path is not a diagnostic, it is a feature, and it needed
  // the evidence bar of one. `readHealth` remains the only thing that can
  // engage the REST fallback, and it only does so on the SDK's own admission
  // that it is serving cache.


  useEffect(() => {
    // Walter 2026-09-12: `readsDead` is once again the ONLY thing that can
    // engage this poll. The `listenerDeaf` verdict used to join it here, and
    // that is what put his window on two disagreeing sources of truth for
    // twenty minutes while its listener was working fine — see the removed
    // watchdog above. Polling is a fallback for a channel the SDK itself
    // admits is dead, not something a heuristic gets to switch on.
    if (!readsDead || !userId || !boardId) return undefined;
    let cancelled = false;
    let sinceMs = 0; // first poll pulls everything, then only what changed
    const tick = async () => {
      const res = await fetchListChanges({ ownerUid: userId, boardId, sinceMs });
      if (cancelled || !res) return;
      if (res.lists.length > 0) {
        logSync('read-via-api', { board: boardId, lists: res.lists.length });
        for (const l of res.lists) {
          const ms = l.updatedAt?.toMillis?.();
          if (ms) lastServerUpdatedAt.current.set(l.id, ms);
        }
        setRawLists((prev) => {
          const byId = new Map(prev.map(l => [l.id, l]));
          for (const l of res.lists) byId.set(l.id, { ...(byId.get(l.id) || {}), ...l });
          return Array.from(byId.values());
        });
      }
      sinceMs = res.serverMs;
    };
    tick();
    const iv = setInterval(tick, 25000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [readsDead, userId, boardId]);

  useEffect(() => {
    if (!userId || !boardId) return;
    const q = collection(db, 'users', userId, 'boards', boardId, 'lists');
    const unsub = onSnapshot(q, (snap) => {
      // Capture server-confirmed updatedAts as conflict baselines. `fromCache`
      // is false when the snapshot reflects what the server has; we only trust
      // those timestamps as a baseline.
      if (!snap.metadata.fromCache) {
        snap.docs.forEach(d => {
          const ts = d.data().updatedAt;
          if (ts && typeof ts.toMillis === 'function') {
            lastServerUpdatedAt.current.set(d.id, ts.toMillis());
          }
        });
      }
      // Did this device just HEAR from the server? `fromCache` false means a
      // genuine server snapshot; true means we're being served our own cache,
      // which is what a blocked Listen channel looks like from in here.
      logHeartbeat('snapshot', boardId, {
        board: boardId,
        lists: snap.docs.length,
        fromCache: snap.metadata.fromCache,
      });
      setRawLists(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      // Permission denial here means the user lost access to this
      // board mid-session (owner revoked their share, or auth state
      // shifted). Without this handler the listener silently dies
      // and `loading` stays true — leaving the board view stuck
      // showing a spinner. Surface as empty + log; BoardPage's own
      // useResolvedBoard error path renders a "you no longer have
      // access" state.
      console.warn('[useLists] snapshot error', err);
      setRawLists([]);
      setLoading(false);
    });
    return unsub;
  }, [userId, boardId]);

  // Overlay any queued offline edits on top of the raw Firestore data so the user
  // always sees their own pending changes, even across reloads while offline.
  const lists = useMemo(() => {
    const queue = loadQueue();
    return rawLists.map(l => {
      const q = queue[l.id];
      if (q && q.boardId === boardId) return { ...l, ...q.patch };
      return l;
    });
    // queueTick forces re-compute after enqueue; loadQueue itself is not reactive.
    // eslint-disable-next-line
  }, [rawLists, boardId, queueTick]);

  // How many edits are saved on THIS DEVICE but not yet accepted by the cloud.
  // Walter 2026-08-12: drives the `unsynced` indicator. Derived from the queue
  // itself rather than from save-status plumbing, so it is true by
  // construction and clears itself the moment a drain succeeds — no state to
  // get out of step with reality. Counts every board, not just this one: an
  // unsent edit elsewhere is still an unsent edit.
  const queuedCount = useMemo(() => {
    try { return Object.keys(loadQueue() || {}).length; } catch { return 0; }
    // eslint-disable-next-line
  }, [queueTick, rawLists]);

  const saveList = useCallback(async (listId, rawData, opts = {}) => {
    if (!userId || !boardId) return;

    // Walter 2026-06-15: the editor threads the 3-way conflict BASE (the
    // cloud content this edit descends from) via a reserved `__baseContent`
    // key on the patch. Pull it off here so it's NEVER written to Firestore,
    // and hand it to the conflict-aware write as baselineContent. See
    // lib/historyConflict.js for why content (not timestamp) is the signal.
    let baselineContent;
    let data = rawData;
    if (rawData && Object.prototype.hasOwnProperty.call(rawData, '__baseContent')) {
      baselineContent = rawData.__baseContent;
      const { __baseContent, ...rest } = rawData;
      void __baseContent;
      data = rest;
    }

    const touchesProse = 'content' in data || 'title' in data;
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

    // Shared helper: stash a prose edit in the conflict-aware queue (used by
    // the offline branch AND as a fallback when an online write fails because
    // navigator.onLine lied about connectivity — Walter 2026-06-22).
    const enqueueProse = () => {
      const proseFields = {};
      const otherFields = {};
      for (const [k, v] of Object.entries(data)) {
        if (k === 'content' || k === 'title') proseFields[k] = v;
        else otherFields[k] = v;
      }
      const baseline = lastServerUpdatedAt.current.get(listId) ?? null;
      const baselineContentForQueue = ('content' in proseFields) ? (baselineContent ?? null) : undefined;
      enqueuePatch(listId, boardId, proseFields, baseline, baselineContentForQueue);
      // Walter 2026-08-12: the moment an edit stops being "saved" and becomes
      // "saved on this device only". Previously nothing recorded it anywhere.
      logSync('queued', {
        list: listId,
        chars: ('content' in proseFields) ? (proseFields.content ?? '').length : 0,
        depth: Object.keys(loadQueue() || {}).length,
      });
      setQueueTick(t => t + 1);
      return otherFields;
    };

    // Offline prose edits: route through the conflict-aware queue instead of
    // letting Firestore auto-replay them (which would silently clobber server changes).
    if (touchesProse && isOffline && listId) {
      const otherFields = enqueueProse();
      // Position/size/z changes are safe to merge — write them straight through.
      if (Object.keys(otherFields).length > 0) {
        const ref = doc(db, 'users', userId, 'boards', boardId, 'lists', listId);
        try {
          await setDoc(ref, {
            ...otherFields,
            updatedAt: serverTimestamp(),
            lastWriteByClientId: getClientId(),
          }, { merge: true });
        } catch { /* offline — non-prose write will replay via Firestore */ }
      }
      return listId;
    }

    // Online prose edit on an existing list: route through the conflict-aware
    // write so that if another device wrote between our baseline and now, the
    // version we're about to overwrite gets snapshotted to history instead of
    // vanishing. Walter 2026-05-16 — "never want a situation where work gets
    // lost." See lib/historyConflict.js for the transaction.
    if (touchesProse && !isOffline && listId) {
      const baseline = lastServerUpdatedAt.current.get(listId) ?? null;
      let result;
      try {
        result = await writeListWithConflictDetection({
          uid: userId,
          boardId,
          listId,
          patch: data,
          baselineUpdatedAtMs: baseline,
          baselineContent,
          actorUid: userId,
        });
      } catch (e) {
        // Walter 2026-06-22: `navigator.onLine` said we were online, but the
        // write (a transaction — needs the server) failed. Almost always this
        // means we're actually offline (the flag lies on some networks / OS
        // states). Preserve the edit in the conflict-aware queue so it's NOT
        // lost and gets reconciled (with conflict detection) on reconnect —
        // exactly the dormant-PC case that produced silent divergence.
        // eslint-disable-next-line no-console
        console.log('[sync] online write FAILED → queueing for reconnect', e?.code || e?.message || e);
        // Walter 2026-08-12: this is THE invisible failure. Three of his
        // conflicts this month started here — a write that failed while
        // genuinely online, queued silently, and replayed hours later against
        // a stale baseline. Record the reason; the indicator now says so too.
        // Walter 2026-08-20: `code` alone was useless — his PC logged
        // `code=Error` dozens of times in a day, which only told us the error
        // carried no Firestore code. Log the MESSAGE (trimmed) too; that's
        // what names the actual failure. Transactions failing while plain
        // writes succeed points at a blocked WebChannel, but let's stop
        // inferring and read it.
        logSync('save-failed', {
          list: listId,
          code: e?.code || e?.name || 'unknown',
          msg: String(e?.message || '').slice(0, 120).replace(/\s+/g, ' '),
          online: typeof navigator !== 'undefined' ? navigator.onLine : '?',
        });
        // Walter 2026-09-06: if that was the SDK's INTERNAL ASSERTION panic,
        // this window's Firestore client is dead and will never deliver
        // another snapshot — it just spent 22 hours showing him a green
        // "synced" over day-old content. Latch it so the header and the
        // banner can tell him to reload. We do NOT stop here: the api
        // fallback below still works, and a stale window that saves is far
        // better than a stale window that doesn't. See lib/clientHealth.
        noteClientError(e);
        // Walter 2026-08-20: before giving up to the queue, try our OWN
        // domain. A content blocker filters firestore.googleapis.com by URL
        // pattern; /api/saveList is first-party and matches no rule, so a
        // Ghostery/Shields user's edit still lands instead of sitting in
        // localStorage for hours. Only reached after a direct write failed,
        // so unblocked users never pay for it.
        const viaApi = await saveViaApi({
          ownerUid: userId,
          boardId,
          listId,
          patch: data,
          baselineContent,
        });
        if (viaApi) {
          logSync('saved-via-api', { list: listId });
          noteSaveSuccess();
          return listId;
        }
        noteSaveFailure();
        enqueueProse();
        return listId;
      }
      // The write landed — clears any failure burst so the banner drops as
      // soon as the path recovers (Walter 2026-08-20).
      noteSaveSuccess();
      // …and record it, throttled. Without this a quiet log is ambiguous:
      // "saved fine" and "never tried" look identical (Walter 2026-08-21).
      logHeartbeat('saved', listId, {
        list: listId,
        chars: ('content' in data) ? (data.content ?? '').length : 0,
      });
      // Walter 2026-08-31: while the content trail is on, keep the full text
      // of every save. The heartbeat above is throttled and records only a
      // size — useless for "two words vanished". Local only, opt-in, off by
      // default. See lib/contentTrail.
      if ('content' in data) recordContent(listId, data.content, 'save');
      if (result.hadConflict && typeof opts.onConflict === 'function') {
        // Fire-and-forget — the caller's toast must not throw or this
        // resolved write would look like a failure.
        try { opts.onConflict(result); } catch (e) { console.warn('[saveList] onConflict cb threw', e); }
      }
      return listId;
    }

    // Normal path: online non-prose edit, or brand-new list creation.
    const ref = listId
      ? doc(db, 'users', userId, 'boards', boardId, 'lists', listId)
      : doc(collection(db, 'users', userId, 'boards', boardId, 'lists'));
    // Walter 2026-05-13: stamp createdAt on brand-new lists. The
    // sidebar's null-order tiebreaker uses it to land newly-created
    // lists at the bottom of the default group; without it, all
    // new lists fall back to id ordering, which is effectively
    // random from the user's perspective.
    const payload = listId
      ? { ...data, updatedAt: serverTimestamp(), lastWriteByClientId: getClientId() }
      : { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), lastWriteByClientId: getClientId() };
    await setDoc(ref, payload, { merge: true });
    return ref.id;
  }, [userId, boardId]);

  // Soft-delete via the `trashList` Cloud Function. Trash entry
  // lives under the board OWNER's uid (regardless of who deleted),
  // so the owner has restore authority — important for shared lists
  // where a guest editor may delete (SHARING_SPEC §6).
  //
  // Walter 2026-05-26 — returns { trashId, remindersTrashed } from
  // the function so callers can wire an undo toast that uses
  // restoreFromTrash (which now re-creates the cascaded reminders
  // too). Returns null if the call failed or the list was already
  // gone (idempotent path on the function side).
  const deleteList = useCallback(async (listId) => {
    if (!userId || !boardId) return null;
    const fn = httpsCallable(functions, 'trashList');
    try {
      const res = await fn({ ownerUid: userId, boardId, listId });
      const data = res?.data || {};
      if (data.alreadyGone) return null;
      return {
        trashId: data.trashId || null,
        remindersTrashed: data.remindersTrashed || 0,
      };
    } catch (e) {
      // Walter 2026-07-19: a FAILED call must be distinguishable from
      // "already gone" — returning null here made the caller show a
      // "deleted" toast while the list sat there untouched (his "delete
      // from the tile dropdown doesn't work" report; on his PC the likely
      // blocker is an ad-blocker eating the cloudfunctions.net call).
      // Walter 2026-08-07: `internal` means BOTH "an extension ate the
      // request" and "the function threw" — the SDK can't tell them apart, so
      // #161's toast had to guess at the ad-blocker. Probe the functions
      // origin and say which one it actually was.
      console.warn('[deleteList] trashList failed:', e?.code, e);
      const { reason, blocked } = await describeCallableFailure(e, { name: 'trashList' });
      return { error: reason, blocked };
    }
  }, [userId, boardId]);

  // Move a list from the current board to another board. Implemented as
  // read → write-at-new-path → delete-at-old-path, preserving the list's ID
  // so any in-flight UI references (jumpTarget, activeListId) stay valid
  // until the source snapshot prunes it from this board's view.
  //
  // Caveat: any queued offline patches keyed on the old boardId in the local
  // offline queue would no longer match post-move. In practice this is rare
  // since moves happen while online; logging it as a known edge case.
  // `newOrder` (optional) positions the list within the target board's
  // sorted list. The caller computes it from the target board's neighbor
  // orders (sidebar's preview-during-drag pattern) so the moved list
  // lands where the user dropped it instead of falling back to whatever
  // intrinsic ordering the target had.
  const moveListToBoard = useCallback(async (listId, targetBoardId, newOrder) => {
    if (!userId || !boardId || !targetBoardId || targetBoardId === boardId) return;
    const srcRef = doc(db, 'users', userId, 'boards', boardId, 'lists', listId);
    const snap = await getDoc(srcRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const dstRef = doc(db, 'users', userId, 'boards', targetBoardId, 'lists', listId);
    const payload = { ...data, updatedAt: serverTimestamp() };
    if (typeof newOrder === 'number' && Number.isFinite(newOrder)) {
      payload.order = newOrder;
    }
    await setDoc(dstRef, payload);
    await deleteDoc(srcRef);
  }, [userId, boardId]);

  // External code (the conflict resolver) can call this after mutating the queue
  // to force the overlay to recompute.
  const refreshQueueOverlay = useCallback(() => setQueueTick(t => t + 1), []);

  return { lists, loading, saveList, deleteList, moveListToBoard, refreshQueueOverlay, queuedCount };
}

// ── ALL LISTS across all boards (one-shot, for on-demand search) ─────
// Not a hook — a plain async function called when the search modal opens.
// Using a one-shot getDocs() instead of a live listener avoids echo-reads on
// every edit and cuts the bulk of this app's Firestore read cost.
//
// Walter 2026-05-19: per-board failures no longer reject the whole batch.
// Previously a single board's getDocs rejection (transient permission
// glitch, network blip) made Promise.all reject and the SearchModal
// hung in "loading…" forever waiting on a settled promise. Now we
// catch per-board, log, and skip — the user gets partial results
// instead of nothing.
export async function fetchAllLists(userId, boards) {
  if (!userId || !boards || boards.length === 0) return [];
  const perBoard = await Promise.all(
    boards.map(async (board) => {
      try {
        const q = collection(db, 'users', userId, 'boards', board.id, 'lists');
        const snap = await getDocs(q);
        return snap.docs.map(d => ({
          id: d.id,
          boardId: board.id,
          boardName: board.name,
          ...d.data(),
        }));
      } catch (err) {
        console.warn('[fetchAllLists] board failed, skipping:', board.id, err);
        return [];
      }
    })
  );
  return perBoard.flat();
}

// One-shot fetch of a single board's lists. Used by the sidebar's
// preview-during-drag flow so we can show a non-active board's list
// rows inline beneath its name without spinning up a live listener
// (the preview is a momentary overlay, not a subscription).
export async function fetchBoardLists(userId, boardId) {
  if (!userId || !boardId) return [];
  const colRef = collection(db, 'users', userId, 'boards', boardId, 'lists');
  const snap = await getDocs(colRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Cross-board single-list read/write. Used by the agenda's reminder
// editing path when a reminder's source list lives on a board OTHER
// than the one the user is currently viewing — useLists is scoped to
// the active board, so saveList there can't reach the right doc.
// Walter 2026-05-06: source-list rewrites silently no-op'd for
// cross-board reminders before this helper existed; both
// updateReminderAndSource (date/text edits) and updateReminderNotes
// (continuation-block edits) now fall back to these.
export async function readListAt(userId, boardId, listId) {
  if (!userId || !boardId || !listId) return null;
  const ref = doc(db, 'users', userId, 'boards', boardId, 'lists', listId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function writeListAt(userId, boardId, listId, partial) {
  if (!userId || !boardId || !listId) return;
  const ref = doc(db, 'users', userId, 'boards', boardId, 'lists', listId);
  await setDoc(
    ref,
    { ...partial, updatedAt: serverTimestamp(), lastWriteByClientId: getClientId() },
    { merge: true },
  );
}
